

import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import axios from "axios";
import toast from "react-hot-toast";
import { 
  
  MdDateRange,

} from "react-icons/md";
import { API_BASE_URL } from "../../../../config";

const EnhancedVisitsStats = () => {
  const [weeklyData, setWeeklyData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewType, setViewType] = useState('both'); // 'weekly', 'monthly', 'both'
  const [, setStats] = useState({
    totalVisits: 0,
    weeklyGrowth: 0,
    monthlyGrowth: 0,
    avgWeeklyVisits: 0
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `${API_BASE_URL}/api/propertyVisit/total-visit-stats`,
          { headers: { token } }
        );

        if (res.data.success) {
          const { weeklyVisits, monthlyVisits } = res.data.result;

          // Format weekly data
          const formattedWeekly = weeklyVisits
            .map((w) => ({
              label: `W${w._id.week}`,
              fullLabel: `Week ${w._id.week} (${w._id.year})`,
              count: w.count,
            }))
            .reverse();

          // Format monthly data
          const monthNames = [
            "Jan","Feb","Mar","Apr","May","Jun",
            "Jul","Aug","Sep","Oct","Nov","Dec"
          ];
          const formattedMonthly = monthlyVisits
            .map((m) => ({
              label: `${monthNames[m._id.month - 1]}`,
              fullLabel: `${monthNames[m._id.month - 1]} ${m._id.year}`,
              count: m.count,
            }))
            .reverse();

          setWeeklyData(formattedWeekly);
          setMonthlyData(formattedMonthly);

          // Calculate statistics
          const totalVisits = weeklyVisits.reduce((sum, w) => sum + w.count, 0);
          const weeklyGrowth = formattedWeekly.length > 1 
            ? ((formattedWeekly[formattedWeekly.length - 1].count - formattedWeekly[formattedWeekly.length - 2].count) / formattedWeekly[formattedWeekly.length - 2].count * 100)
            : 0;
          const monthlyGrowth = formattedMonthly.length > 1
            ? ((formattedMonthly[formattedMonthly.length - 1].count - formattedMonthly[formattedMonthly.length - 2].count) / formattedMonthly[formattedMonthly.length - 2].count * 100)
            : 0;
          const avgWeeklyVisits = totalVisits / formattedWeekly.length;

          setStats({
            totalVisits,
            weeklyGrowth,
            monthlyGrowth,
            avgWeeklyVisits
          });

        } else {
          toast.error(res.data.message || "Failed to fetch stats");
        }
      } catch (error) {
        // console.error(error);
        toast.error("Error fetching stats");
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const getChartOptions = () => {
    let categories = [];
    if (viewType === 'weekly') categories = weeklyData.map(d => d.label);
    else if (viewType === 'monthly') categories = monthlyData.map(d => d.label);
    else categories = weeklyData.map(d => d.label);

    return {
      chart: {
        type: "line",
        toolbar: { show: false },
        background: "transparent",
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
        },
        dropShadow: {
          enabled: true,
          color: '#000',
          top: 18,
          left: 7,
          blur: 10,
          opacity: 0.1
        }
      },
      stroke: {
        width: viewType === 'both' ? [3, 2] : [3],
        curve: "smooth",
      },
      colors: viewType === 'both' 
        ? ["#3b82f6", "#f59e0b"] 
        : viewType === 'weekly' 
          ? ["#3b82f6"] 
          : ["#f59e0b"],
      dataLabels: { 
        enabled: false 
      },
      markers: {
        size: viewType === 'both' ? [6, 4] : [6],
        colors: ["#fff"],
        strokeColors: viewType === 'both' ? ["#3b82f6", "#f59e0b"] : viewType === 'weekly' ? ["#3b82f6"] : ["#f59e0b"],
        strokeWidth: 2,
        hover: {
          size: 8
        }
      },
      grid: {
        borderColor: '#e2e8f0',
        strokeDashArray: 4,
        xaxis: { lines: { show: true } },
        yaxis: { lines: { show: true } },
      },
      xaxis: {
        categories,
        labels: { 
          style: { 
            colors: "#64748b",
            fontSize: '12px',
            fontWeight: 500
          } 
        },
        axisBorder: { show: false },
        axisTicks: { show: false }
      },
      yaxis: {
        labels: { 
          style: { 
            colors: "#64748b",
            fontSize: '12px'
          },
          formatter: (val) => Math.round(val)
        },
        min: 0
      },
      tooltip: { 
        shared: true, 
        intersect: false,
        theme: 'light',
        style: {
          fontSize: '12px',
          fontFamily: 'Inter, sans-serif'
        },
        y: {
          formatter: (val) => `${val} visits`
        }
      },
      legend: { 
        show: viewType === 'both',
        position: "top",
        horizontalAlign: 'right',
        labels: { 
          colors: "#64748b",
          useSeriesColors: false
        },
        markers: {
          width: 8,
          height: 8,
          radius: 4
        }
      },
      fill: {
        type: viewType === 'both' ? ['gradient', 'solid'] : ['gradient'],
        gradient: {
          shade: 'light',
          type: "vertical",
          shadeIntensity: 0.25,
          gradientToColors: viewType === 'both' ? ["#60a5fa", "#fbbf24"] : viewType === 'weekly' ? ["#60a5fa"] : ["#fbbf24"],
          inverseColors: false,
          opacityFrom: 0.8,
          opacityTo: 0.3,
          stops: [0, 100],
        },
      },
    };
  };

  const getChartSeries = () => {
    if (viewType === 'weekly') {
      return [{
        name: "Weekly Visits",
        type: "area",
        data: weeklyData.map(d => d.count),
      }];
    } else if (viewType === 'monthly') {
      return [{
        name: "Monthly Visits",
        type: "area", 
        data: monthlyData.map(d => d.count),
      }];
    } else {
      return [
        {
          name: "Weekly Visits",
          type: "area",
          data: weeklyData.map(d => d.count),
        },
        {
          name: "Monthly Visits",
          type: "line",
          data: monthlyData.map(d => d.count),
        }
      ];
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded w-1/3 mb-4"></div>
          <div className="h-64 bg-gray-300 dark:bg-gray-600 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
      {/* Header Section */}
      {/* <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
              <MdInsights className="text-3xl" />
              Visits Analytics
            </h2>
            <p className="text-blue-100">Track your property visit trends and performance</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">{stats.totalVisits}</div>
            <div className="text-sm text-blue-100">Total Visits</div>
          </div>
        </div>
      </div> */}

      {/* Stats Cards */}
      {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6 bg-gray-50 dark:bg-gray-700">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Weekly Growth</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-white">
                {stats.weeklyGrowth > 0 ? '+' : ''}{stats.weeklyGrowth.toFixed(1)}%
              </p>
            </div>
            <div className={`p-3 rounded-full ${stats.weeklyGrowth >= 0 ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
              {stats.weeklyGrowth >= 0 ? <MdTrendingUp size={24} /> : <MdTrendingDown size={24} />}
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Monthly Growth</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-white">
                {stats.monthlyGrowth > 0 ? '+' : ''}{stats.monthlyGrowth.toFixed(1)}%
              </p>
            </div>
            <div className={`p-3 rounded-full ${stats.monthlyGrowth >= 0 ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
              {stats.monthlyGrowth >= 0 ? <MdTrendingUp size={24} /> : <MdTrendingDown size={24} />}
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Avg Weekly</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-white">
                {stats.avgWeeklyVisits.toFixed(1)}
              </p>
            </div>
            <div className="p-3 rounded-full bg-blue-100 text-blue-600">
              <MdVisibility size={24} />
            </div>
          </div>
        </div>
      </div> */}

      {/* Chart Controls */}
      <div className="px-4 pt-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Visit Trends
          </h3>
          <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setViewType('both')}
              className={`px-2 py-2 rounded-md text-sm font-medium transition-all ${
                viewType === 'both'
                  ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <MdDateRange className="inline-block mr-1" />
              Both
            </button>
            <button
              onClick={() => setViewType('weekly')}
              className={`px-2 py-2 rounded-md text-sm font-medium transition-all ${
                viewType === 'weekly'
                  ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Weekly
            </button>
            <button
              onClick={() => setViewType('monthly')}
              className={`px-2 py-2 rounded-md text-sm font-medium transition-all ${
                viewType === 'monthly'
                  ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Monthly
            </button>
          </div>
        </div>
      </div>

      {/* Chart Section */}
      <div className="px-2 pb-2">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-2">
          <Chart 
            options={getChartOptions()} 
            series={getChartSeries()} 
            type="line" 
            height={400} 
          />
        </div>
      </div>

      {/* Quick Insights */}
      {/* <div className="px-6 pb-6">
        <div className="bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-700 dark:to-gray-600 rounded-xl p-4">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
            <MdInsights />
            Quick Insights
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="text-gray-700 dark:text-gray-300">
              <span className="font-medium">Peak Performance:</span> {
                weeklyData.length > 0 
                  ? weeklyData.reduce((max, curr) => curr.count > max.count ? curr : max).fullLabel
                  : 'No data'
              }
            </div>
            <div className="text-gray-700 dark:text-gray-300">
              <span className="font-medium">Current Trend:</span> {
                stats.weeklyGrowth > 10 ? '📈 Growing Strong' :
                stats.weeklyGrowth > 0 ? '📊 Steady Growth' :
                stats.weeklyGrowth > -10 ? '📉 Slight Decline' : '⚠️ Needs Attention'
              }
            </div>
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default EnhancedVisitsStats;


